import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";

import { ReadOnlyNotice } from "@/components/ReadOnlyNotice";
import { Badge } from "@/components/StatusBadge";
import { DataTable } from "@/components/table/DataTable";
import type { DataTableColumn } from "@/components/table/columns";
import { Button } from "@/components/ui/Button";
import { ApiError, apiFetch } from "@/lib/api";
import type { MunicipalityResource, Page } from "@/lib/api-types";
import { useIsOwner } from "@/lib/current-actor";

import { AddMunicipalityDialog } from "./AddMunicipalityDialog";

/**
 * `/settings/municipalities` (Story 15.1). A pure frontend addition — every
 * route this screen calls (`GET`/`POST /municipalities`) already shipped,
 * untouched by this story (`app/api/municipalities.py`). Mirrors
 * `PropertiesList.tsx`'s own List+Record shape exactly: the shared
 * `DataTable` (Epic 2), an owner-gated "Add" action (`useIsOwner` — `POST
 * /municipalities` is `role=Role.OWNER`, the identical withholding
 * mechanism `PropertiesList.tsx` uses for `POST /properties`, not merely
 * "not Read-only": a Member is not Read-only and would otherwise see this
 * button, then 403 the moment they submitted), and a per-row link to the
 * already-existing, untouched `/municipalities/:id` record page.
 *
 * `GET /municipalities` is deliberately small and unpaginated by design
 * (its own docstring: "no sort/filter/page grammar is offered, mirroring
 * `GET /alert-rules`'s own identical 'small and fixed' precedent") — this
 * screen fetches the whole list in one plain request with no query string
 * at all, the identical shape `AlertsList.tsx` already established as
 * `/settings/alerts`'s own precedent for `GET /alert-rules`. No
 * client-side search/filter (spec judgement call 2, left out): not
 * required by the AC, and a real portfolio's own municipality count is
 * small enough that scanning the table directly is the whole interaction —
 * this can be added later without disturbing anything here if that
 * assumption stops holding.
 */
export function MunicipalitiesList() {
  const navigate = useNavigate();
  const isOwner = useIsOwner();
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    document.title = "Municipalities — PropDesk";
  }, []);

  //: The same query key `AddPropertyWizard.tsx`'s own municipality picker
  //: already uses for this identical `GET /municipalities` fetch — sharing
  //: it means a cache this screen or the wizard already populated is reused
  //: rather than re-fetched, and either one invalidating it keeps both in
  //: sync.
  const listQueryKey = ["municipalities", "list"] as const;
  const listQuery = useQuery({
    queryKey: listQueryKey,
    queryFn: () => apiFetch<Page<MunicipalityResource>>("/municipalities"),
    placeholderData: keepPreviousData,
  });

  const columns = useMemo<DataTableColumn<MunicipalityResource>[]>(
    () => [
      { key: "name", sortable: false },
      { key: "county", sortable: false },
      {
        key: "setup_complete",
        sortable: false,
        //: The exceptional state only — mirrors `PropertiesList.tsx`'s own
        //: "Archived" badge (rendered only for the archived row, nothing
        //: extra for a live one) and `BackupStatus.tsx`'s identical boolean
        //: `is_overdue` -> `Badge` precedent: a fully set-up town shows no
        //: badge at all, not a second "Set up" badge nobody needs to see.
        render: (row) => (row.setup_complete ? null : <Badge label="Setup incomplete" fill="attention" />),
      },
    ],
    [],
  );

  const errorMessage = listQuery.error instanceof ApiError ? listQuery.error.message : undefined;

  return (
    <div className="mx-auto max-w-content-max">
      <ReadOnlyNotice className="mb-4" />
      <div className="mb-2 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-title font-semibold tracking-title text-ink md:hidden">Municipalities</h1>
          <p className="mt-2 text-body text-ink-secondary">
            Every town in the portfolio, and whether its compliance details are set up.
          </p>
        </div>
        {isOwner ? (
          <Button type="button" onClick={() => setCreateOpen(true)} className="w-full md:w-auto">
            Add a municipality
          </Button>
        ) : null}
      </div>

      <DataTable
        caption="Municipalities"
        itemNamePlural="municipalities"
        columns={columns}
        page={listQuery.data}
        isPending={listQuery.isPending}
        isFetching={listQuery.isFetching}
        isError={listQuery.isError}
        {...(errorMessage ? { errorMessage } : {})}
        getRowHref={(row) => `/municipalities/${row.id}`}
        emptyState={
          isOwner
            ? {
                heading: "No municipalities yet.",
                body: "Add the first town to start tracking its compliance details.",
                action: { label: "Add a municipality", onClick: () => setCreateOpen(true) },
              }
            : {
                heading: "No municipalities yet.",
                body: "Municipalities will appear here once an Owner adds one.",
              }
        }
      />

      {isOwner ? (
        <AddMunicipalityDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreated={(id) => navigate(`/municipalities/${id}`)}
        />
      ) : null}
    </div>
  );
}
